import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { Search, Bell, Menu } from "lucide-react";
import { Input } from "@/shared/ui/Input";

interface TopbarProps {
  title: string;
  // Opens the mobile nav sheet (<768px). The menu button is hidden from md up.
  onMenu: () => void;
  menuOpen: boolean;
}

// Sticky topbar: page h1 + 300px global search + notification icon button.
// Below md it becomes 56px: menu button + truncated title + bell (search is hidden —
// it has no behaviour yet, and at phone width it would crush the title).
export const Topbar = forwardRef<HTMLButtonElement, TopbarProps>(function Topbar(
  { title, onMenu, menuOpen },
  menuRef,
) {
  const { t } = useTranslation();
  return (
    <header className="sticky top-0 z-20 flex h-14 flex-none items-center gap-2 border-b border-grey-200 bg-white/85 px-3 backdrop-blur-md md:h-16 md:gap-4 md:px-7">
      <button
        ref={menuRef}
        type="button"
        onClick={onMenu}
        aria-label={t("topbar.openMenu")}
        aria-expanded={menuOpen}
        aria-controls="mobile-nav"
        className="flex h-10 w-10 flex-none items-center justify-center rounded-control text-grey-700 transition-transform hover:bg-grey-50 active:scale-[0.96] md:hidden"
      >
        <Menu size={22} strokeWidth={1.8} />
      </button>

      <h1 className="min-w-0 truncate text-[17px] font-bold tracking-[-0.3px] text-grey-900 md:text-xl">{title}</h1>

      {/* Search box — leading icon kept; pl-[38px] preserves room for the Search icon. */}
      <div className="relative ml-auto hidden w-[300px] max-w-[34vw] md:block">
        <Search
          size={18}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-grey-400"
        />
        <Input
          type="search"
          placeholder={t("topbar.searchPlaceholder")}
          className="h-10 pl-[38px] pr-3 text-[13.5px]"
        />
      </div>

      <button
        type="button"
        aria-label={t("topbar.alerts")}
        className="relative ml-auto flex h-10 w-10 flex-none items-center justify-center rounded-control border border-grey-200 bg-white text-grey-600 transition-colors hover:bg-grey-50 hover:text-grey-900 md:ml-0"
      >
        <Bell size={20} strokeWidth={1.8} />
        <span className="absolute right-[9px] top-2 h-2 w-2 rounded-pill border-2 border-white bg-red-bright" />
      </button>
    </header>
  );
});
